import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PlatformModules from '../../../../core/accessControl/Modules';
import { Button } from '../../../../core/components/atoms/Button';
import { Typography } from '../../../../core/components/molecules/Typography';
import { Modal } from '../../../../core/components/templates/Modal';
import { useScreenSize } from '../../../../core/hooks/useScreenSize';
import { useWindowBreakpoints } from '../../../../core/hooks/useWindowBreakpoints';
import { hasPermission } from '../../../../core/stores/accessControlStore';
import { useUserStore } from '../../../Auth/stores/authStore';
import { useDropCourse } from '../../hooks/useDropCourse';
import { useRegisterCourses } from '../../hooks/useRegisterCourse';
import { useMyRegularCourses } from '../../hooks/useRegularCourse';
import { useRetakeCourse } from '../../hooks/useRetakeCourse';
import { useCoursesStore } from '../../stores/courseStore';
import { AdminDropRegularCourseModal } from '../AdminDropRegularCourseModal';
import { DropRegularCourseModal } from '../DropRegularCourseModal';

interface CourseActionsProps {
  courseType: 'regular' | 'mini' | 'regional';
  courseStatus: 'notstarted' | 'inprogress' | 'finished';
  openCourseProgramModal?: () => void;
  slug: string;
  showDropButton?: boolean;
  moveHeaderOnMobile?: boolean;
  isLanding?: boolean;
  courseName?: string;
  pdfSummaryUri?: string;
}

const CourseActions = ({
  courseType,
  courseStatus,
  slug,
  openCourseProgramModal,
  showDropButton = true,
  moveHeaderOnMobile = false,
  isLanding = false,
  courseName = '',
  pdfSummaryUri
}: CourseActionsProps) => {
  const me = useUserStore();
  const isAdmin = me.user?.role.includes('admin') ?? false;

  const screenSize = useScreenSize();
  const windowBreakpoints = useWindowBreakpoints();

  const navigate = useNavigate();
  const { mutate, isLoading } = useRegisterCourses();
  useMyRegularCourses();
  const { mutate: mutateDrop, isLoading: isLoadingDrop } = useDropCourse();
  const { mutateRetake, isLoadingRetake } = useRetakeCourse();
  const { user } = useUserStore();
  const {
    setUserMatriculatedCourse,
    setCourseShow,
    setShowAllCourses,
    userMatriculatedCourse,
  } = useCoursesStore();

  const handleClickButton = () => {
    navigate(`/cursos/${slug}`);
  };

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openAbandonModal, setOpenAbandonModal] = useState<boolean>(false);
  const [adminOpenAbandonModal, setAdminOpenAbandonModal] =
    useState<boolean>(false);
  const [openRegisterModal, setOpenRegisterModal] = useState<boolean>(false);

  const handleClickEmenta = () => {
    if(pdfSummaryUri && pdfSummaryUri.includes("http")) {
      window.open(pdfSummaryUri, '_blank')
    } else if (openCourseProgramModal) {
      openCourseProgramModal();
    }
  }

  const handleRegister = () => {
    mutate(slug, {
      onSuccess: () => {
        navigate(`/cursos/${slug}`);
      },
      onError: (error: any) => {
        const { response } = error;
        if (response?.status === 422) {
          mutateRetake(slug, {
            onSuccess: () => {
              navigate(`/cursos/${slug}`);
            },
          });
        }
      },
    });
  };

  const handleOpenRevision = () => {
    navigate(`/cursos/${slug}/revisao`);
  };

  const handleDrop = (reasons?: string[]) => {
    mutateDrop(
      {
        slug,
        reasons,
      },
      {
        onSuccess: () => {
          setOpenAbandonModal(false);
          setUserMatriculatedCourse(null);
          setCourseShow(null);
          setShowAllCourses();
          window.location.reload();
        },
        onError: (error) => {
          console.log(error);
        },
      }
    );
  };

  const handleRegisterConfirmation = () => {
    if (
      userMatriculatedCourse &&
      userMatriculatedCourse.slug !== slug &&
      userMatriculatedCourse.kind === 'regular' &&
      !isAdmin
    ) {
      setOpenModal(true);
    } else {
      setOpenRegisterModal(true);
    }
  };

  const renderButtons = () => {
    if (slug === 'esquadrao-elite') {
      const hasEsquadrao = hasPermission([PlatformModules.ESQUADRAO.ID]);

      if (!hasEsquadrao) {
        return <></>;
      }
    }

    if (courseType !== 'regular') {
      switch (courseStatus) {
        case 'notstarted':
          return (
            <Button
              variant='contained-tonal'
              loading={isLoadingRetake || isLoading}
              onClick={handleRegister}
              className='w-full sm:w-auto'
              full={screenSize < windowBreakpoints.sm}
            >
              Iniciar curso
            </Button>
          );
        case 'inprogress':
          return (
            <Button
              onClick={handleClickButton}
              className='w-full bg-feedback-color-warning-pure sm:w-auto'
              full={screenSize < windowBreakpoints.sm}
            >
              Continuar o curso
            </Button>
          );
        case 'finished':
          return (
            <Button
              onClick={handleClickButton}
              className='w-full bg-feedback-color-success-pure sm:w-auto'
              full={screenSize < windowBreakpoints.sm}
            >
              Recomeçar curso
            </Button>
          );
        default:
          return null;
      }
    } else if (courseType === 'regular') {
      switch (courseStatus) {
        case 'notstarted':
          return (
            <Button
              variant='contained-tonal'
              loading={isLoadingRetake || isLoading}
              onClick={handleRegisterConfirmation}
              className='w-full sm:w-auto'
              full={screenSize < windowBreakpoints.sm}
            >
              Fazer matrícula
            </Button>
          );
        case 'inprogress':
          return (
            <>
              <Button
                onClick={handleClickButton}
                LeftIcon='play_arrow'
                iconVariant='rounded'
                variant='contained-tonal'
                className='w-full sm:w-auto'
                full={screenSize < windowBreakpoints.sm}
              >
                Continue de onde parou
              </Button>
              {!isLanding && (
                <Button
                  onClick={handleOpenRevision}
                  LeftIcon='playlist_add_check'
                  iconVariant='rounded'
                  variant='outlined'
                  className='w-full text-card-color-default sm:w-auto'
                  full={screenSize < windowBreakpoints.sm}
                >
                  Acessar revisão
                </Button>
              )}
            </>
          );
        case 'finished':
          return (
            <Button
              onClick={handleClickButton}
              LeftIcon='refresh'
              className='w-full bg-feedback-color-success-pure sm:w-auto'
              full={screenSize < windowBreakpoints.sm}
            >
              Revisar conteúdo
            </Button>
          );
        default:
          return null;
      }
    }

    return null;
  };

  const handleOpenCourseLanding = () => {
    const url = `/cursos/${userMatriculatedCourse.slug}/detalhes`;
    navigate(url);
  };

  const handleAbandon = () => {
    if (isAdmin) {
      setAdminOpenAbandonModal(true);
      return;
    }
    setOpenAbandonModal(true);
  };

  return (
    <div className='flex flex-wrap gap-lg text-center'>
      {renderButtons()}
      {courseStatus === 'inprogress' && !isLanding && (
        <Button
          variant='text'
          className={`w-full sm:w-auto`}
          size='small'
          negative={
            !user?.isDarkMode
              ? screenSize >= windowBreakpoints.md || !moveHeaderOnMobile
              : false
          }
          onClick={handleOpenCourseLanding}
          full={screenSize < windowBreakpoints.sm}
        >
          Detalhes do curso
        </Button>
      )}
      {isLanding && (
        <Button
          variant='outlined'
          className={`w-full sm:w-auto`}
          onClick={handleClickEmenta}
          LeftIcon='playlist_add_check'
          negative={!user?.isDarkMode}
          full={screenSize < windowBreakpoints.sm}
        >
          Ver ementa do curso
        </Button>
      )}
      {courseStatus === 'inprogress' && showDropButton && (
        <Button
          variant='text'
          className={`w-full sm:w-auto`}
          negative={!user?.isDarkMode}
          onClick={handleAbandon}
          loading={isLoadingDrop}
          LeftIcon='exit_to_app'
          full={screenSize < windowBreakpoints.sm}
        >
          Abandonar curso
        </Button>
      )}
      <Modal
        isOpen={openRegisterModal}
        onClose={() => setOpenRegisterModal(false)}
        position='center'
        className='w-[35%] p-0'
      >
        <div className='flex flex-col gap-lg'>
          <Typography>
            Tem certeza que deseja fazer sua matrícula neste curso?
          </Typography>
          <div className='flex flex-row items-center justify-end gap-sm'>
            <Button variant='text' onClick={() => setOpenRegisterModal(false)}>
              Voltar
            </Button>
            <Button
              variant='text'
              onClick={() => handleRegister()}
              className='text-feedback-color-success-pure'
            >
              Fazer matrícula
            </Button>
          </div>
        </div>
      </Modal>

      <DropRegularCourseModal
        isOpen={openAbandonModal}
        onClose={() => setOpenAbandonModal(false)}
        isLoading={isLoadingDrop}
        onDrop={handleDrop}
        courseName={courseName}
      />

      <AdminDropRegularCourseModal
        isOpen={adminOpenAbandonModal}
        onClose={() => setAdminOpenAbandonModal(false)}
        onDrop={handleDrop}
        userMatriculatedCourse={userMatriculatedCourse}
      />

      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        position='center'
        className='w-[35%] p-0'
      >
        <div className='flex flex-col gap-lg'>
          <Typography>
            Você já está matriculado em um curso regular. Ao se inscrever neste
            novo curso, você será automaticamente desinscrito do curso em que
            está matriculado atualmente.
          </Typography>
          <div className='flex flex-row items-center justify-end gap-sm'>
            <Button variant='text' onClick={() => setOpenModal(false)}>
              Voltar
            </Button>
            <Button
              variant='text'
              onClick={() => {
                setOpenModal(false);
                handleRegister();
              }}
              className='text-feedback-color-success-pure'
            >
              Fazer matrícula
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CourseActions;
